import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Fade,
  Zoom,
  Slide,
  Grow,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Help as HelpIcon,
  Send as SendIcon,
  Close as CloseIcon,
  SupportAgent as SupportAgentIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import Grid2 from '@mui/material/Grid2';
import PageHeader from '../../components/PageHeader';
import { CONTACT_INFO } from '../../utils/common';
import { useSelector } from 'react-redux';
import { API_BASE_URL } from '../../services/api';
import { httpService } from '../../services/httpService';

interface FAQ {
  id: number;
  question: string;
  answer: string;
  category: string;
}

interface SupportTicket {
  subject: string;
  priority: string;
  category: string;
  description: string;
}

const Support: React.FC = () => {
  const navigate = useNavigate();
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const [ticketForm, setTicketForm] = useState<SupportTicket>({
    subject: '',
    priority: 'medium',
    category: 'general',
    description: '',
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);

  const faqs: FAQ[] = [
    {
      id: 1,
      question: 'How do I reset my password?',
      answer: 'To reset your password, click the user icon located at the top-right corner of the header, open the menu, and select Profile Settings. Scroll down to the Security Settings section, where you will find the Change Password button. Click it and enter your old password, your new password, and confirm the new password. After filling in the details, submit the form to successfully update your password.',
      category: 'Account',
    },
    {
      id: 2,
      question: 'How do I change my profile information?',
      answer: 'To update your profile information, click the user icon located at the top-right corner of the header, open the menu, and select Profile Settings page, where you will find a section labeled Personal Information. On the right side of this section, click the Edit button to enable editing. Modify the details you want to update, and once you have made the necessary changes, click the Save button to apply and store your updated profile information.',
      category: 'Account',
    },
    {
      id: 3,
      question: 'What browsers are supported?',
      answer: 'Sampark Dashboard supports the latest versions of Chrome, Firefox, Safari, and Edge browsers. For the best experience, we recommend using Chrome or Firefox.',
      category: 'Technical',
    },
    {
      id: 4,
      question: 'How do I add new members to my team?',
      answer: 'Go to the Members page and click on "Add Member" button. Fill in the required information and send an invitation to the new member.',
      category: 'Team Management',
    },
    {
      id: 5,
      question: 'Is my data secure?',
      answer: 'Yes, we use industry-standard encryption and security measures to protect your data. All data is encrypted in transit and at rest.',
      category: 'Security',
    },
    {
      id: 6,
      question: 'How can I export my data?',
      answer: 'You can export your data from the Dashboard page. Click on the export button and select the format you prefer (CSV, PDF, or Excel).',
      category: 'Data Management',
    },
  ];

  const handleInputChange = (field: keyof SupportTicket) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    setTicketForm(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmitTicket = async () => {
    try {
      // Reset error state
      setSubmitError(null);
      // Start loading
      setIsLoading(true);

      // Console log the ticket form details
      console.log('=== Support Ticket Submitted ===');
      console.log('Full Form Data:', ticketForm);
      console.log('User Details:', userDetails);
      console.log('================================');
      
      const payload = {
        data: {
          ...ticketForm,
          user_email: userDetails?.email_id || '',
          user_name: userDetails?.user_full_name || '',
        }
      }
      const sendQueryResponse = await httpService.post<any>(`${API_BASE_URL}/user/sendCustomerQuery`, payload);
      console.log("Send Query Response printed ::", sendQueryResponse);
      
      // Stop loading
      setIsLoading(false);

      if (sendQueryResponse.data.status === 200) {
        setSubmitSuccess(true);
        setOpenDialog(false);
        setTicketForm({
          subject: '',
          priority: 'medium',
          category: 'general',
          description: '',
        });
        // Hide success message after 5 seconds
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        // Handle non-200 status codes
        const errorMessage = sendQueryResponse.data.message || 'Failed to submit ticket';
        setSubmitError(errorMessage);
      }
    } catch (error: any) {
      // Stop loading on error
      setIsLoading(false);
      
      // Handle API error response
      console.error('Error submitting ticket:', error);
      
      if (error.response?.data) {
        const errorData = error.response.data;
        const errorMessage = errorData.error?.errorDetails || errorData.message || 'Failed to send query';
        setSubmitError(errorMessage);
      } else if (error.message) {
        setSubmitError(error.message);
      } else {
        setSubmitError('An unexpected error occurred. Please try again.');
      }
    }
  };

  const categoryColors: { [key: string]: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' } = {
    'Account': 'primary',
    'Technical': 'secondary',
    'Team Management': 'success',
    'Security': 'error',
    'Data Management': 'warning',
  };

  return (
    <Box sx={{ width: '100%', mx: 'auto' }}>
      <Box>
        <Box sx={{ mb: 2 }}>
          <PageHeader
            heading="Support Center"
            subheading="Get help and support for your account and billing issues."
            variant="gradient"
          />
        </Box>

        <Fade in={submitSuccess} timeout={500}>
          <Box sx={{ mb: 3 }}>
            {submitSuccess && (
              <Alert
                severity="success"
                icon={<CheckCircleIcon />}
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(76, 175, 80, 0.2)',
                  animation: 'slideDown 0.3s ease-out',
                  '@keyframes slideDown': {
                    '0%': { transform: 'translateY(-20px)', opacity: 0 },
                    '100%': { transform: 'translateY(0)', opacity: 1 },
                  }
                }}
              >
                Your support ticket has been submitted successfully! We'll get back to you within 24 hours.
              </Alert>
            )}
          </Box>
        </Fade>

        <Fade in={!!submitError} timeout={500}>
          <Box sx={{ mb: 3 }}>
            {submitError && (
              <Alert
                severity="error"
                icon={<CloseIcon />}
                onClose={() => setSubmitError(null)}
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(244, 67, 54, 0.2)',
                  animation: 'slideDown 0.3s ease-out',
                  '@keyframes slideDown': {
                    '0%': { transform: 'translateY(-20px)', opacity: 0 },
                    '100%': { transform: 'translateY(0)', opacity: 1 },
                  }
                }}
              >
                {submitError}
              </Alert>
            )}
          </Box>
        </Fade>

        {/* Loading Backdrop */}
        <Backdrop
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          }}
          open={isLoading}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress color="inherit" size={60} thickness={4} />
            <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>
              Submitting your ticket...
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
              Please wait
            </Typography>
          </Box>
        </Backdrop>

        <Grid2 container spacing={3}>
          {/* Hero Section with Quick Actions */}
          <Grid2 size={12}>
            <Paper
              elevation={0}
              sx={{
                p: 4,
                mb: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: 3,
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -50,
                  right: -50,
                  width: 200,
                  height: 200,
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '50%',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -30,
                  left: -30,
                  width: 150,
                  height: 150,
                  background: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '50%',
                }
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <SupportAgentIcon sx={{ fontSize: 40, mr: 2 }} />
                  <Typography variant="h4" fontWeight={700}>
                    How can we help you today?
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ mb: 4, opacity: 0.9 }}>
                  Choose your preferred way to get in touch with our support team
                </Typography>

                <Grid2 container spacing={2}>
                  {[
                    { icon: EmailIcon, title: 'Submit Ticket', desc: 'Detailed assistance', color: '#2196F3', index: 0, action: () => setOpenDialog(true) },
                    { icon: PhoneIcon, title: 'Call Us', desc: CONTACT_INFO.mobileNumber, color: '#FF9800', index: 1 },
                    { icon: HelpIcon, title: 'Email Support', desc: CONTACT_INFO.supportEmail, color: '#9C27B0', index: 2 }
                  ].map((item) => (
                    <Grid2 key={item.index} size={{ xs: 12, sm: 6, md: 4 }}>
                      <Grow in timeout={300 + item.index * 100}>
                        <Card
                          onMouseEnter={() => setHoveredCard(item.index)}
                          onMouseLeave={() => setHoveredCard(null)}
                          onClick={item.action}
                          sx={{
                            height: '100%',
                            cursor: 'pointer',
                            background: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: 2,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            transform: hoveredCard === item.index ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
                            boxShadow: hoveredCard === item.index
                              ? `0 12px 40px ${item.color}40`
                              : '0 2px 8px rgba(0,0,0,0.1)',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: 4,
                              background: item.color,
                              transform: hoveredCard === item.index ? 'scaleX(1)' : 'scaleX(0)',
                              transition: 'transform 0.3s ease',
                            }
                          }}
                        >
                          <CardContent sx={{ textAlign: 'center', p: 3 }}>
                            <Box
                              sx={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                background: `${item.color}20`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto',
                                mb: 2,
                                transition: 'all 0.3s ease',
                                transform: hoveredCard === item.index ? 'rotate(360deg) scale(1.1)' : 'rotate(0) scale(1)',
                              }}
                            >
                              <item.icon sx={{ fontSize: 32, color: item.color }} />
                            </Box>
                            <Typography variant="h6" fontWeight={600} color="text.primary" gutterBottom>
                              {item.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                              {item.desc}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grow>
                    </Grid2>
                  ))}
                </Grid2>
              </Box>
            </Paper>
          </Grid2>

          {/* Contact Information - Modern Card Design */}
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                height: 'fit-content',
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
                border: '1px solid',
                borderColor: 'divider',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                }
              }}
            >
              <Typography variant="h6" fontWeight={700} sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <SupportAgentIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                Contact Information
              </Typography>

              {[
                {
                  icon: EmailIcon,
                  title: 'Email Support',
                  value: CONTACT_INFO.supportEmail,
                  subtitle: CONTACT_INFO.responseTime.email,
                  color: '#2196F3',
                  bgColor: '#E3F2FD'
                },
                {
                  icon: PhoneIcon,
                  title: 'Phone Support',
                  value: CONTACT_INFO.phoneSupport.number,
                  subtitle: CONTACT_INFO.phoneSupport.availability,
                  color: '#FF9800',
                  bgColor: '#FFF3E0'
                },
                {
                  icon: LocationIcon,
                  title: 'Office Address',
                  value: CONTACT_INFO.organizationAddress.street,
                  subtitle: `${CONTACT_INFO.organizationAddress.city}, ${CONTACT_INFO.organizationAddress.state} ${CONTACT_INFO.organizationAddress.zipCode}`,
                  color: '#9C27B0',
                  bgColor: '#F3E5F5'
                },
                {
                  icon: HelpIcon,
                  title: 'Privacy Policy',
                  value: 'Data Protection',
                  subtitle: 'View our privacy policy',
                  color: '#2196F3',
                  bgColor: '#E3F2FD',
                  isClickable: true,
                  onClick: () => navigate('/privacy-policy')
                },
                {
                  icon: HelpIcon,
                  title: 'Terms & Conditions',
                  value: 'Terms of Service',
                  subtitle: 'View our terms & conditions',
                  color: '#4CAF50',
                  bgColor: '#E8F5E8',
                  isClickable: true,
                  onClick: () => navigate('/terms')
                }
              ].map((item, index) => (
                <Zoom in timeout={300 + index * 100} key={index}>
                  <Box
                    onClick={item.onClick ? item.onClick : undefined}
                    sx={{
                      mb: 2.5,
                      p: 2.5,
                      borderRadius: 2,
                      background: item.bgColor,
                      border: '1px solid',
                      borderColor: `${item.color}30`,
                      transition: 'all 0.3s ease',
                      textAlign: 'center',
                      cursor: item.isClickable ? 'pointer' : 'default',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 8px 24px ${item.color}40`,
                        borderColor: item.color,
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 50,
                          height: 50,
                          borderRadius: 2,
                          background: `${item.color}20`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                        }}
                      >
                        <item.icon sx={{ fontSize: 24, color: item.color }} />
                      </Box>
                      <Typography variant="subtitle2" fontWeight={600} color="text.primary" gutterBottom>
                        {item.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={{
                          fontWeight: 500,
                          mb: 0.5,
                          wordBreak: 'break-word'
                        }}
                      >
                        {item.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ScheduleIcon sx={{ fontSize: 14, mr: 0.5 }} />
                        {item.subtitle}
                      </Typography>
                    </Box>
                  </Box>
                </Zoom>
              ))}
            </Paper>
          </Grid2>

          {/* FAQ Section - Modern Accordion Design */}
          <Grid2 size={{ xs: 12, md: 8 }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center' }}>
                  <HelpIcon sx={{ mr: 1.5, color: 'primary.main' }} />
                  Frequently Asked Questions
                </Typography>
                <Chip
                  label={`${faqs.length} FAQs`}
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 600
                  }}
                />
              </Box>

              <Box sx={{ mt: 2 }}>
                {faqs.map((faq, index) => (
                  <Fade in timeout={300 + index * 50} key={faq.id}>
                    <Accordion
                      sx={{
                        mb: 1.5,
                        borderRadius: '12px !important',
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:before': { display: 'none' },
                        '&:first-of-type': { borderRadius: '12px !important' },
                        '&:last-of-type': { borderRadius: '12px !important' },
                        boxShadow: 'none',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                          transform: 'translateY(-2px)',
                        },
                        '&.Mui-expanded': {
                          margin: '0 0 12px 0',
                          boxShadow: '0 8px 24px rgba(102, 126, 234, 0.15)',
                        }
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'transform 0.3s ease',
                            }}
                          >
                            <ExpandMoreIcon sx={{ color: 'white' }} />
                          </Box>
                        }
                        sx={{
                          px: 2.5,
                          py: 1,
                          '& .MuiAccordionSummary-content': {
                            my: 1.5,
                            alignItems: 'center',
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              background: `${categoryColors[faq.category] || 'primary'}.light`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Typography variant="caption" fontWeight={700} color="primary">
                              {index + 1}
                            </Typography>
                          </Box>
                          <Typography
                            variant="subtitle1"
                            fontWeight={600}
                            sx={{ flexGrow: 1, pr: 2 }}
                          >
                            {faq.question}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 2.5, pb: 2.5 }}>
                        <Box
                          sx={{
                            py: 1,
                            borderLeft: '3px solid',
                            borderColor: 'primary.main',
                            background: 'rgba(102, 126, 234, 0.04)',
                            borderRadius: 1,
                            pl: 2,
                            pr: 2,
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ lineHeight: 1.7, textAlign: 'left' }}
                          >
                            {faq.answer}
                          </Typography>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  </Fade>
                ))}
              </Box>
            </Paper>
          </Grid2>
        </Grid2>

        {/* Support Ticket Dialog - Modern Design */}
        <Dialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          maxWidth="md"
          fullWidth
          TransitionComponent={Slide}
          TransitionProps={{ direction: 'up' } as any}
          PaperProps={{
            sx: {
              borderRadius: 3,
              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9ff 100%)',
            }
          }}
        >
          <DialogTitle sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            position: 'relative',
            py: 3,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <EmailIcon sx={{ mr: 1.5, fontSize: 28 }} />
              <Typography variant="h5" fontWeight={700}>
                Submit Support Ticket
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
              We'll get back to you within 24 hours
            </Typography>
            <IconButton
              onClick={() => setOpenDialog(false)}
              sx={{
                position: 'absolute',
                right: 12,
                top: 12,
                color: 'white',
                background: 'rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.3)',
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: 3, pb: 2 }}>
            {submitError && (
              <Alert 
                severity="error" 
                onClose={() => setSubmitError(null)}
                sx={{ mb: 2, borderRadius: 2 }}
              >
                {submitError}
              </Alert>
            )}
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="Subject"
                value={ticketForm.subject}
                onChange={handleInputChange('subject')}
                sx={{
                  mb: 2.5,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderWidth: 2,
                    }
                  }
                }}
                placeholder="Brief description of your issue"
              />

              <Grid2 container spacing={2} sx={{ mb: 2.5 }}>
                <Grid2 size={6}>
                  <FormControl fullWidth>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={ticketForm.priority}
                      label="Priority"
                      onChange={handleInputChange('priority')}
                      sx={{
                        borderRadius: 2,
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderWidth: 2,
                        }
                      }}
                    >
                      <MenuItem value="low">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#4CAF50' }} />
                          Low
                        </Box>
                      </MenuItem>
                      <MenuItem value="medium">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#FF9800' }} />
                          Medium
                        </Box>
                      </MenuItem>
                      <MenuItem value="high">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#F44336' }} />
                          High
                        </Box>
                      </MenuItem>
                      <MenuItem value="urgent">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#9C27B0' }} />
                          Urgent
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid2>
                <Grid2 size={6}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={ticketForm.category}
                      label="Category"
                      onChange={handleInputChange('category')}
                      sx={{
                        borderRadius: 2,
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderWidth: 2,
                        }
                      }}
                    >
                      <MenuItem value="general">General</MenuItem>
                      <MenuItem value="technical">Technical Issue</MenuItem>
                      <MenuItem value="account">Account & Billing</MenuItem>
                      <MenuItem value="feature">Feature Request</MenuItem>
                      <MenuItem value="bug">Bug Report</MenuItem>
                    </Select>
                  </FormControl>
                </Grid2>
              </Grid2>

              <TextField
                fullWidth
                multiline
                rows={5}
                label="Description"
                placeholder="Please describe your issue in detail..."
                value={ticketForm.description}
                onChange={handleInputChange('description')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderWidth: 2,
                    }
                  }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
            <Button
              onClick={() => setOpenDialog(false)}
              sx={{
                borderRadius: 2,
                px: 3,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitTicket}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              disabled={!ticketForm.subject || !ticketForm.description || isLoading}
              sx={{
                borderRadius: 2,
                px: 3,
                textTransform: 'none',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5568d3 0%, #6a3f91 100%)',
                  boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)',
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  background: '#ccc',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {isLoading ? 'Submitting...' : 'Submit Ticket'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Support;
